import { Badge } from '@chakra-ui/react'
import type { ComponentWithAs, IconProps } from '@chakra-ui/react'
import type { History, Location } from 'history'
import moment from 'moment'
import { OnChangeValue } from 'react-select'
import type { Req } from 'use-http'
import ImageKit from 'imagekit-javascript'
import {
  BooleanFilterValue,
  CustomMetadataResponse,
  DateFilterValue,
  DimensionsFilterValue,
  FieldIdentifier,
  FilterValidationObject,
  FormatFilterValue,
  KeywordsFilterValue,
  LocationFilterValue,
  MediaLibraryFormProps,
  MediaLibraryErrorResponse,
  MediaLibrarySearchFileResponse,
  NameFilterValue,
  NumberFilterValue,
  Option,
  PrivacyFilterValue,
  PublishedFilterValue,
  SearchFilterGroup,
  SearchFilterJson,
  SearchFilterValue,
  SearchQueryJson,
  SelectFilterValue,
  SizeFilterValue,
  TagsFilterValue,
  TextFilterValue,
  TransparencyFilterValue,
  UserSessionResponse,
  DescriptionFilterValue,
  IdFilterValue,
  PathFilterValue,
} from '../app/interfaces'
import type { FileDetailsResponse } from '../app/interfaces/FileDetailsResponse'
import {
  CSSIcon,
  DOCIcon,
  EOTIcon,
  ICOIcon,
  JSIcon,
  M3U8Icon,
  OTFIcon,
  PDFIcon,
  SWFIcon,
  TSIcon,
  TTFIcon,
  TXTIcon,
  VideoIcon,
  WOFF2Icon,
  WOFFIcon,
  XLSIcon,
  JSONIcon,
  XMLIcon,
  AudioIcon,
} from '../graphics/media-library/file-icons'
import { removeLeadingSlash, removeTrailingSlash } from './urlHelpers'
import { fileConstraints } from '../app/components/MediaLibrary/NewButton/uploadUtils'
import { PostTransformationTypes } from '../app/constants'
import { CheckValidObjectId } from '../app/components/MediaLibrary/utils'

const numericRegex = /^[-+]?[0-9]+(\.[0-9]+)?$/u
// checks for valid distance values `${numberValue}${"cm/m/km"}` eg. 5.5km
const distanceRegex = /^((\d+)(\.\d+)?)(m|cm|km)$/u
// checks for valid coordinates in the form of `${latitude},${longitude}`
const coordinateRegex =
  /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/u

const fetchSignedUrl = async (
  urlPrefix: string,
  selectedFileId: string,
  request: Req<string>,
  trArray: Array<{ [key: string]: string }> = [],
  extraQueryParams: { updatedAt?: number } = {},
  expirationTimestamp?: number,
  urlFor: string = ""
): Promise<string | MediaLibraryErrorResponse> =>
  request.post(`${urlPrefix}api/v2/files/${selectedFileId}/signedUrl`, {
    tr: trArray,
    extraQueryParams,
    expirationTimestamp,
    urlFor,
  })

export const formatDuration = (seconds: number): string => {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }
  return `${m}:${String(s).padStart(2, '0')}`
}

export const customCNameExists = (
  user: UserSessionResponse | null,
): boolean => {
  if (user?.firstCustomCname) {
    return true
  }
  return false
}

const getCnameUrlIfRequired = ({
  fileURL,
  imagekitId,
  firstCustomCname,
  transformation,
}: {
  fileURL: string
  imagekitId: string
  firstCustomCname?: UserSessionResponse["firstCustomCname"]
  transformation?: Array<{ [key: string]: string }>
}): string => {
  if (!firstCustomCname) {
    return fileURL
  }

  let urlObj: URL | undefined

  const customCname = firstCustomCname

  try {
    const CName = removeLeadingSlash(removeTrailingSlash(customCname))

    urlObj = new URL(fileURL)
    urlObj.hostname = CName
    urlObj.port = ''

    urlObj.pathname = urlObj.pathname.replace(`/${imagekitId}/`, '/')

    if (transformation) {
      const imagekit = new ImageKit({
        urlEndpoint: firstCustomCname,
        transformationPosition: 'query',
        publicKey: 'does-not-matter',
      })
      return imagekit.url({
        src: urlObj.toString(),
        transformation,
      })
    }

    return urlObj.toString()
  } catch (err) {
    return fileURL
  }
}

/* ********************* SEARCH QUERY - PORT FROM OLD DASHBOARD *************** */
export const dimensionOptions: {
  [presetDimension: string]: {
    unit: string
    height: {
      min?: number
      max?: number
    }
    width: {
      min?: number
      max?: number
    }
    text: string
  }
} = {
  Icons: {
    unit: 'px',
    height: {
      max: 256,
    },
    width: {
      max: 256,
    },
    text: 'Icons',
  },
  Small: {
    unit: 'px',
    height: {
      min: 256,
      max: 480,
    },
    width: {
      min: 256,
      max: 640,
    },
    text: 'Small',
  },
  Medium: {
    unit: 'px',
    height: {
      min: 480,
      max: 768,
    },
    width: {
      min: 640,
      max: 1024,
    },
    text: 'Medium',
  },
  Large: {
    unit: 'px',
    height: {
      min: 768,
      max: 1080,
    },
    width: {
      min: 1024,
      max: 1920,
    },
    text: 'Large',
  },
  'Larger than 400×300': {
    unit: 'px',
    height: {
      min: 300,
    },
    width: {
      min: 400,
    },
    text: 'Larger than 400×300',
  },
  'Larger than 640×480': {
    unit: 'px',
    height: {
      min: 480,
    },
    width: {
      min: 640,
    },
    text: 'Larger than 640×480',
  },
  'Larger than 800×600': {
    unit: 'px',
    height: {
      min: 600,
    },
    width: {
      min: 800,
    },
    text: 'Larger than 800×600',
  },
  'Larger than 1024×768': {
    unit: 'px',
    height: {
      min: 768,
    },
    width: {
      min: 1024,
    },
    text: 'Larger than 1024×768',
  },
  'Larger than 1600×1200': {
    unit: 'px',
    height: {
      min: 1200,
    },
    width: {
      min: 1600,
    },
    text: 'Larger than 1600×1200',
  },
  'Larger than 1920×1200': {
    unit: 'px',
    height: {
      min: 1200,
    },
    width: {
      min: 1920,
    },
    text: 'Larger than 1920×1200',
  },
}

export const privacyOptions = {
  'Only show non-private files': false,
  'Only show private files': true,
  'Show all files (include private files)': 'ALL',
}

export const publishedOptions = {
  'Only show unpublished files': false,
  'Only show published files': true,
  'Show all files (including unpublished files)': 'ALL',
}

export const transparencyOptions = {
  'Only show files with no transparency': false,
  'Only show files with transparency': true,
  'Show all files (include transparent files)': 'ALL',
}

// maps search filters operators to display text
export const operatorsMapping: {
  [operator: string]: {
    text: string
    query: string | null
  }
} = {
  IS: {
    text: 'is',
    query: '=',
  },
  IS_ONE_OF: {
    text: 'is one of',
    query: 'IN',
  },
  IS_NOT: {
    text: 'is not',
    query: 'NOT =',
  },
  IS_NONE_OF: {
    text: 'is none of',
    query: 'NOT IN',
  },
  STARTS_WITH: {
    text: 'starts with',
    query: ':',
  },
  SLIDER: {
    text: 'is between',
    query: null,
  },
  GREATER_THAN: {
    text: 'is greater than',
    query: '>',
  },
  LESS_THAN: {
    text: 'is less than',
    query: '<',
  },
  GREATER_THAN_EQUAL: {
    text: 'is greater than equal to',
    query: '>=',
  },
  LESS_THAN_EQUAL: {
    text: 'is less than equal to',
    query: '<=',
  },
  BETWEEN: {
    text: 'range (inclusive)',
    query: null,
  },
  EXACTLY: {
    text: 'exactly',
    query: '=',
  },
  EARLIER_THAN: {
    text: 'earlier than',
    query: '<',
  },
  LATER_THAN: {
    text: 'later than',
    query: '>',
  },
  LAST_7_DAYS: {
    text: 'last 7 days',
    query: '>',
  },
  LAST_30_DAYS: {
    text: 'last 30 days',
    query: '>',
  },
  CUSTOM_RANGE: {
    text: 'custom range',
    query: null,
  },
  CUSTOM_DIMENSIONS: {
    text: 'custom values',
    query: null,
  },
  GEO_POINT: {
    text: 'geo near',
    query: '=',
  },
  GEO_BOX: {
    text: 'geo box',
    query: '=',
  },
  GEO_POLYGON: {
    text: 'geo polygon',
    query: '=',
  },
  HAS: {
    text: 'contains',
    query: "HAS",
  },
  IS_EMPTY: {
    text: 'is empty',
    query: 'NOT EXISTS',
  },
  IS_NOT_EMPTY: {
    text: 'is not empty',
    query: 'EXISTS',
  }
}

export const getMaxFileSize = (unit: string): number => {
  switch (unit) {
    case 'MB':
      return 200
    default:
      return 1024
  }
}

export const fileTypeOptionsMapping = {
  images: 'Images',
  videos: 'Videos',
  cssJs: 'CSS/JS',
  others: 'Others',
}

// Storing NOT IN options in Others
export const fileTypeMapping = {
  images: ['jpg', 'webp', 'png', 'gif', 'svg', 'avif', 'ico'],
  videos: ['mp4', 'm4v', 'webm', 'mov'],
  cssJs: ['js', 'css', 'ts'],
  others: [
    'jpg',
    'webp',
    'png',
    'gif',
    'svg',
    'avif',
    'ico',
    'mp4',
    'm4v',
    'webm',
    'mov',
    'js',
    'css',
    'ts',
  ],
}

export const extractSingleField = (
  searchQueryJson: SearchQueryJson,
  fieldName: string,
): SearchFilterValue | undefined => {
  let foundValue: SearchFilterValue | undefined
  searchQueryJson.filterGroups.forEach(group => {
    group.expressions.forEach(expression => {
      if (expression.fieldName === fieldName) {
        foundValue = expression.value
      }
    })
  })
  return foundValue
}

const getValueString = ({
  expression,
  rangeValueType,
  customMetadataField,
}: {
  expression: SearchFilterJson
  rangeValueType?: string
  customMetadataField?: CustomMetadataResponse
}): string => {
  const { fieldName, operator, value } = expression

  // createdBy stores {value, label} objects or plain strings — extract value for query strings
  if (fieldName === 'createdBy') {
    if (['IS', 'IS_NOT'].includes(operator)) {
      const strVal = typeof value === 'string' ? value : (value as { value: string; label: string })?.value || ''
      return `"${strVal}"`
    }
    if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
      const items = Array.isArray(value) ? value : []
      return `[${items.map(item => `"${typeof item === 'string' ? item : item.value}"`).toString()}]`
    }
    return ''
  }

  if (
    [
      'IS',
      'IS_NOT',
      'STARTS_WITH',
      'LESS_THAN',
      'GREATER_THAN',
      'GREATER_THAN_EQUAL',
      'LESS_THAN_EQUAL',
      'HAS',
    ].includes(operator)
  ) {
    if (
      ['name', 'id', 'type', 'fileType', 'format', 'tags', 'description', 'path'].includes(fieldName) ||
      (customMetadataField?.schema &&
        ['Text', 'Textarea'].includes(customMetadataField.schema.type))
    ) {
      return `"${value as string}"`
    }
    if (
      customMetadataField?.schema &&
      ['Number', 'Boolean'].includes(
        customMetadataField.schema.type,
      )
    ) {
      return value as string
    }
    if (
      customMetadataField?.schema &&
      ['SingleSelect'].includes(customMetadataField.schema.type)
    ) {
      if (typeof value === 'string') {
        return `"${value}"`
      }
      return value as unknown as string
    }
    if (['transparency', 'private', 'published'].includes(fieldName)) {
      return value as string
    }
  }

  if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
    if (
      [
        'id',
        'name',
        'type',
        'fileType',
        'format',
        'tags',
        'path',
        '"embeddedMetadata.Keywords"',
      ].includes(fieldName) ||
      (customMetadataField?.schema &&
        ['Text', 'Textarea'].includes(customMetadataField.schema.type))
    ) {
      return `[${(value as string[]).map(val => `"${val}"`).toString()}]`
    }
    if (
      customMetadataField?.schema &&
      ['MultiSelect', 'SingleSelect'].includes(customMetadataField.schema.type)
    ) {
      const sanitisedValues = (value as Array<string | boolean | number>).map(
        val => {
          if (typeof val === 'string') {
            return `"${val}"`
          }
          return val
        },
      )
      return `[${sanitisedValues.toString()}]`
    }
  }

  if (['LATER_THAN', 'EARLIER_THAN'].includes(operator)) {
    if (
      [
        'createdAt',
        'updatedAt',
        '"embeddedMetadata.DateTimeOriginal"',
      ].includes(fieldName) ||
      (customMetadataField?.schema &&
        ['Date'].includes(customMetadataField.schema.type))
    ) {
      let date
      const typeAssertedValue = value as DateFilterValue
      if ('exact' in typeAssertedValue) {
        if (operator === 'EARLIER_THAN') {
          date = moment(typeAssertedValue.exact).startOf('day').toISOString()
        } else if (operator === 'LATER_THAN') {
          date = moment(typeAssertedValue.exact).endOf('day').toISOString()
        }
      }
      return `"${date}"`
    }
  }

  if (['SLIDER'].includes(operator)) {
    const typeAssertedValue = value as SizeFilterValue
    const limitValue =
      rangeValueType === 'UPPER'
        ? typeAssertedValue.range.max
        : typeAssertedValue.range.min

    if (['size'].includes(fieldName)) {
      return `"${limitValue}${typeAssertedValue.unit}"`
    }
  }

  if (['BETWEEN'].includes(operator)) {
    const typeAssertedValue = value as { min: string; max: string }
    const limitValue =
      rangeValueType === 'UPPER' ? typeAssertedValue.max : typeAssertedValue.min
    return limitValue
  }

  if (['CUSTOM_RANGE'].includes(operator)) {
    if (
      [
        'createdAt',
        'updatedAt',
        '"embeddedMetadata.DateTimeOriginal"',
      ].includes(fieldName) ||
      (customMetadataField?.schema &&
        ['Date'].includes(customMetadataField.schema.type))
    ) {
      const typeAssertedValue = value as { to: string; from: string }
      let limitDate = ''
      if (rangeValueType === 'UPPER') {
        limitDate = moment(typeAssertedValue.to).endOf('day').toISOString()
      } else if (rangeValueType === 'LOWER') {
        limitDate = moment(typeAssertedValue.from).startOf('day').toISOString()
      }
      const dateString = limitDate
      return `"${dateString}"`
    }
  }

  if (['GEO_POINT', 'GEO_BOX', 'GEO_POLYGON'].includes(operator)) {
    return `"${expression.value as string}"`
  }

  return ''
}

const getExpressionString = ({
  expression,
  customMetadataField,
}: {
  expression: SearchFilterJson
  customMetadataField?: CustomMetadataResponse
}): string => {
  const { fieldName, operator, value } = expression
  if (fieldName === 'transparency') {
    const typeAssertedValue = value as TransparencyFilterValue
    if (typeof transparencyOptions[typeAssertedValue] === 'boolean') {
      return `${fieldName} = ${transparencyOptions[typeAssertedValue]}`
    }
    return `${fieldName} IN [true, false]`
  } else if (fieldName === 'private') {
    const typeAssertedValue = value as PrivacyFilterValue
    if (typeof privacyOptions[typeAssertedValue] === 'boolean') {
      return `${fieldName} = ${privacyOptions[typeAssertedValue]}`
    }
    return `${fieldName} IN [true, false]`
  } else if (fieldName === 'published') {
    const typeAssertedValue = value as PublishedFilterValue
    if (typeof publishedOptions[typeAssertedValue] === 'boolean') {
      return `${fieldName} = ${publishedOptions[typeAssertedValue]}`
    }
    return `${fieldName} IN [true, false]`
  } else if (fieldName === 'path') {
    // Handle path field with proper query syntax
    if (operator === 'IS') {
      return `path = "${value as string}"`
    } else if (operator === 'IS_NOT') {
      return `path NOT = "${value as string}"`
    } else if (operator === 'STARTS_WITH') {
      return `path: "${value as string}"`
    } else if (operator === 'IS_ONE_OF') {
      const values = Array.isArray(value) ? value as string[] : [value as string]
      return `path IN [${values.map(v => `"${v}"`).join(', ')}]`
    } else if (operator === 'IS_NONE_OF') {
      const values = Array.isArray(value) ? value as string[] : [value as string]
      return `path NOT IN [${values.map(v => `"${v}"`).join(', ')}]`
    } else if (operator === 'HAS') {
      return `path HAS "${value as string}"`
    }
    // Fallback to default handling
    return `${fieldName} ${operatorsMapping[operator].query} "${value as string}"`
  } else if (fieldName === 'dimensions') {
    let heightString = ''
    let widthString = ''
    let dimensionValue:
      | DimensionsFilterValue
      | {
          unit: string
          height: {
            min?: number | undefined
            max?: number | undefined
          }
          width: {
            min?: number | undefined
            max?: number | undefined
          }
          text: string
        }

    if (operator === 'CUSTOM_DIMENSIONS') {
      dimensionValue = value as {
        height: { min: string }
        width: { min: string }
      }
    } else {
      dimensionValue = dimensionOptions[value as string]
    }

    if (dimensionValue?.height?.min) {
      heightString += `height >= ${dimensionValue.height.min}`
      if ('max' in dimensionValue.height) {
        heightString += ` AND height <= ${dimensionValue.height.max}`
      }
    } else if (dimensionValue?.height && 'max' in dimensionValue.height) {
      heightString += `height <= ${dimensionValue.height.max}`
    }

    if (dimensionValue?.width?.min) {
      widthString += `width >= ${dimensionValue.width.min}`
      if ('max' in dimensionValue.width) {
        widthString += ` AND width <= ${dimensionValue.width.max}`
      }
    } else if (dimensionValue?.width && 'max' in dimensionValue.width) {
      widthString += `width <= ${dimensionValue.width.max}`
    }

    if (heightString && widthString) {
      return `(${heightString} AND ${widthString})`
    } else if (heightString) {
      return `(${heightString})`
    } else if (widthString) {
      return `(${widthString})`
    }
  } else if (operator === 'SLIDER') {
    const lowerLimitString = `${fieldName} >= ${getValueString({
      expression,
      rangeValueType: 'LOWER',
    })}`
    const upperLimitString = `${fieldName} <= ${getValueString({
      expression,
      rangeValueType: 'UPPER',
    })}`
    return `(${lowerLimitString} AND ${upperLimitString})`
  } else if (operator === 'CUSTOM_RANGE') {
    const lowerLimitString = `${fieldName} > ${getValueString({
      expression,
      rangeValueType: 'LOWER',
      customMetadataField,
    })}`
    const upperLimitString = `${fieldName} <= ${getValueString({
      expression,
      rangeValueType: 'UPPER',
      customMetadataField,
    })}`
    return `(${lowerLimitString} AND ${upperLimitString})`
  } else if (operator === 'EXACTLY') {
    const typeAssertedValue = value as { exact: string }
    const lowerDate = moment(typeAssertedValue.exact)
      .startOf('day')
      .toISOString()
    const upperDate = moment(typeAssertedValue.exact).endOf('day').toISOString()
    const lowerLimitString = `${fieldName} > "${lowerDate}"`
    const upperLimitString = `${fieldName} <= "${upperDate}"`
    return `(${lowerLimitString} AND ${upperLimitString})`
  } else if (['LAST_7_DAYS', 'LAST_30_DAYS'].includes(operator)) {
    let limitDate
    if (operator === 'LAST_7_DAYS') {
      limitDate = moment().subtract(7, 'days').startOf('day').toISOString()
    } else if (operator === 'LAST_30_DAYS') {
      limitDate = moment().subtract(30, 'days').startOf('day').toISOString()
    }
    return `${fieldName} > "${limitDate}"`
  } else if (['BETWEEN'].includes(operator)) {
    const lowerLimitString = `(${fieldName} >= ${getValueString({
      expression,
      rangeValueType: 'LOWER',
    })})`
    const upperLimitString = `(${fieldName} <= ${getValueString({
      expression,
      rangeValueType: 'UPPER',
    })})`
    return `${lowerLimitString} AND ${upperLimitString}`
  } else if (['IS_EMPTY', 'IS_NOT_EMPTY'].includes(operator)) {
    // Terminal operators - no value needed
    return `${fieldName} ${operatorsMapping[operator].query}`
  } else {
    return `${fieldName} ${operatorsMapping[operator].query} ${getValueString({
      expression,
      customMetadataField,
    })}`
  }
  return '()'
}

const getGroupString = ({
  filterGroup,
  customMetadataFieldsMapping,
}: {
  filterGroup: SearchFilterGroup
  customMetadataFieldsMapping?: { [fieldName: string]: CustomMetadataResponse }
}): string => {
  const totalExpressions = filterGroup.expressions.length
  const boolean = totalExpressions > 1 ? filterGroup.boolean : ''

  return `(${filterGroup.expressions
    .map(expression =>
      getExpressionString({
        expression,
        customMetadataField:
          customMetadataFieldsMapping?.[expression.fieldName],
      }),
    )
    .join(` ${boolean.toUpperCase()} `)})`
}

export const getSearchQueryFromJSON = ({
  searchQueryJson,
  customMetadataFieldsMapping,
}: {
  searchQueryJson: SearchQueryJson
  customMetadataFieldsMapping?: { [fieldName: string]: CustomMetadataResponse }
}): string => {
  const totalGroups = searchQueryJson.filterGroups.length
  const boolean = totalGroups > 1 ? searchQueryJson.boolean : ''

  return searchQueryJson.filterGroups
    .map(filterGroup =>
      getGroupString({ filterGroup, customMetadataFieldsMapping }),
    )
    .join(` ${boolean.toUpperCase()} `)
}


const getDateForDisplay = (date): string =>
  moment(date).format('ddd, MMMM Do YYYY')

export const getFilterText = ({
  expression,
}: {
  expression: {
    fieldName: string
    operator: string
    value: SearchFilterValue
  }
}): string => {
  const { fieldName, operator, value } = expression

  let filterText = ''

  if (['transparency', 'private', 'published'].includes(fieldName)) {
    filterText = value as string
  } else if (fieldName === 'path') {
    if (['IS', 'IS_NOT', 'STARTS_WITH'].includes(operator)) {
      const folderName = (value as string).split('/').filter(Boolean).pop() ?? (value as string)
      filterText = ` ${operatorsMapping[operator].text} ${folderName}`
    } else if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
      const folderNames = Array.isArray(value) 
        ? value.map(path => (path as string).split('/').filter(Boolean).pop() ?? path).join(', ')
        : ''
      filterText = ` ${operatorsMapping[operator].text} ${folderNames}`
    }
  } else if (
    [
      'IS',
      'IS_NOT',
      'STARTS_WITH',
      'GREATER_THAN',
      'LESS_THAN',
      'GREATER_THAN_EQUAL',
      'LESS_THAN_EQUAL',
      'HAS'
    ].includes(operator)
  ) {
    filterText = ` ${operatorsMapping[operator].text} ${value as string}`
  } else if (['BETWEEN'].includes(operator)) {
    const typeAssertedValue = value as { min: string; max: string }
    filterText = ` is between ${typeAssertedValue.min} and ${typeAssertedValue.max} (inclusive)`
  } else if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
    filterText = ` ${operatorsMapping[operator].text} ${
      Array.isArray(value) ? value.join(', ') : ''
    }`
  } else if (['SLIDER'].includes(operator)) {
    const typeAssertedValue = value as SizeFilterValue
    const low = typeAssertedValue.range.min
    const high = typeAssertedValue.range.max
    const unit = typeAssertedValue.unit ? typeAssertedValue.unit : ''
    filterText = ` ${operatorsMapping[operator].text} ${low} ${unit} and ${high} ${unit}`
  } else if (['dimensions'].includes(fieldName)) {
    if (operator === 'CUSTOM_DIMENSIONS') {
      const typeAssertedValue = value as {
        height: { min: string }
        width: { min: string }
      }
      if (typeAssertedValue?.height?.min || typeAssertedValue?.width?.min) {
        filterText = ' is custom configured'
      }
    } else if (
      dimensionOptions[value as string] &&
      dimensionOptions[value as string].text
    ) {
      filterText = ` is ${dimensionOptions[value as string].text}`
    }
  } else if (['EXACTLY', 'EARLIER_THAN', 'LATER_THAN'].includes(operator)) {
    const typeAssertedValue = value as { exact: string }
    if (typeAssertedValue.exact) {
      filterText = ` is ${operatorsMapping[operator].text} ${getDateForDisplay(
        typeAssertedValue.exact,
      )}`
    }
  } else if (['LAST_30_DAYS', 'LAST_7_DAYS'].includes(operator)) {
    filterText = ` is within ${operatorsMapping[operator].text}`
  } else if (['CUSTOM_RANGE'].includes(operator)) {
    const typeAssertedValue = value as { to: string; from: string }
    if (typeAssertedValue.from && typeAssertedValue.to) {
      filterText = ` is between ${getDateForDisplay(
        typeAssertedValue.from,
      )} and ${getDateForDisplay(typeAssertedValue.to)}`
    }
  } else if (['GEO_POINT', 'GEO_BOX', 'GEO_POLYGON'].includes(operator)) {
    filterText = ` is ${value as string}`
  } else if (['IS_EMPTY', 'IS_NOT_EMPTY'].includes(operator)) {
    // Terminal operators - no value needed, just display the operator text
    filterText = ` ${operatorsMapping[operator].text}`
  }
  return filterText
}

/* ********************* SEARCH QUERY ***************** */
export const staticFieldNames = {
  embeddedMetadataFields: [
    '"embeddedMetadata.DateTimeOriginal"',
    '"embeddedMetadata.Keywords"',
    '"embeddedMetadata.LocationTaken"',
  ],
  otherFields: [
    'id',
    'name',
    'tags',
    'size',
    'dimensions',
    'format',
    'private',
    'published',
    'transparency',
    'createdAt',
    'updatedAt',
    'description',
    'path',
    'createdBy',
  ],
  mediaCollectionDefaultFilters: ['name'],
}

export const getFieldsMapping = ({
  fieldName,
  customMetadataField,
}: FieldIdentifier): {
  displayText: string
  possibleValues?: string[] | Array<string | boolean | number>
  possibleOperators?: string[]
  possibleUnits?: string[]
  getInitialValue(operator?: string): SearchFilterValue
} => {

  if (fieldName) {
    if (fieldName === "fileType") {
      return {
        displayText: 'File Type',
        possibleOperators: ['IS', 'IS_NOT', 'IS_ONE_OF', 'IS_NONE_OF'],
        getInitialValue: (operator?: string): any => {
          if (!operator) {
            return ''
          }
          if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
            return []
          } else if (['IS', 'IS_NOT'].includes(operator)) {
            return ''
          }
          return ''
        },
      }
    } else if (fieldName === 'name') {
      return {
        displayText: 'Name',
        possibleOperators: [
          'IS',
          'IS_NOT',
          'IS_ONE_OF',
          'IS_NONE_OF',
          'STARTS_WITH',
          'HAS'
        ],
        getInitialValue: (operator?: string): NameFilterValue => {
          if (!operator) {
            return ''
          }
          if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
            return []
          } else if (['IS', 'IS_NOT', 'STARTS_WITH', 'HAS'].includes(operator)) {
            return ''
          }
          return ''
        },
      }
    }
    if (fieldName === 'id') {
      return {
        displayText: 'Asset Id',
        possibleOperators: ['IS', 'IS_NOT', 'IS_ONE_OF', 'IS_NONE_OF'],
        getInitialValue: (operator?: string): NameFilterValue => {
          if (!operator) {
            return ''
          }
          if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
            return []
          } else if (['IS', 'IS_NOT'].includes(operator)) {
            return ''
          }
          return ''
        },
      }
    }
    if (fieldName === 'tags') {
      return {
        displayText: 'Tags',
        possibleOperators: ['IS_ONE_OF', 'IS_NONE_OF', 'HAS', 'IS_EMPTY', 'IS_NOT_EMPTY'],
        getInitialValue: (operator?: string): TagsFilterValue => {
          if (!operator) {
            return []
          }
          if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
            return []
          } else if (['HAS'].includes(operator)) {
            return ''
          } else if (['IS_EMPTY', 'IS_NOT_EMPTY'].includes(operator)) {
            return null as any
          }
          return []
        }
      }
    }

    if (fieldName === 'size') {
      return {
        displayText: 'Size',
        possibleUnits: ['B', 'KB', 'MB'],
        possibleOperators: ['SLIDER'],
        getInitialValue: (): SizeFilterValue => ({
          unit: 'KB',
          range: {
            min: 0,
            max: getMaxFileSize('KB'),
          },
        }),
      }
    }

    if (fieldName === 'dimensions') {
      return {
        displayText: 'File dimensions',
        possibleValues: Object.keys(dimensionOptions),
        possibleOperators: ['IS', 'CUSTOM_DIMENSIONS'],
        getInitialValue: (operator?: string): DimensionsFilterValue => {
          if (!operator || ['IS'].includes(operator)) {
            return ''
          } else if (['CUSTOM_DIMENSIONS'].includes(operator)) {
            return {
              width: {
                min: '',
              },
              height: {
                min: '',
              },
            }
          }
          return ''
        },
      }
    }

    if (fieldName === 'format') {
      return {
        displayText: 'Format',
        possibleValues: [
          'jpg',
          'webp',
          'png',
          'gif',
          'svg',
          'avif',
          'pdf',
          'js',
          'woff2',
          'woff',
          'ttf',
          'otf',
          'eot',
          'css',
          'txt',
          'mp4',
          'm4v',
          'webm',
          'mov',
          'swf',
          'ts',
          'm3u8',
          'ico',
          'json',
          'xml',
          'mp3',
          'm4a',
          'mjs',
          'php',
          'rss',
          'doc',
          'docx',
          'ppt',
          'pptx',
          'eps',
          'psd',
          'xls',
          'xlsx',
          'rtf',
          'bmp',
          'tif',
          'glb',
          'heif',
          'heic',
          'dxf',
          'ai',
          'ept',
          'py',
          'obj',
          '3ds',
          'stl',
          'bw',
          'djvu',
          'dng',
          'ps',
          'eps3',
          'fbx',
          'flif',
          'gltf',
          'indd',
          'ply',
          'arw',
          'wav',
          'csv',
          'yaml',
          'appimage',
        ],
        possibleOperators: ['IS', 'IS_NOT', 'IS_ONE_OF', 'IS_NONE_OF'],
        getInitialValue: (operator?: string): FormatFilterValue => {
          if (!operator || ['IS', 'IS_NOT'].includes(operator)) {
            return ''
          } else if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
            return []
          }
          return ''
        },
      }
    }

    if (fieldName === 'private') {
      return {
        displayText: 'File privacy',
        getInitialValue: (): PrivacyFilterValue => '',
      }
    }

    if (fieldName === 'published') {
      return {
        displayText: 'File published',
        getInitialValue: (): PublishedFilterValue => '',
      }
    }

    if (fieldName === 'transparency') {
      return {
        displayText: 'File transparency',
        getInitialValue: (): TransparencyFilterValue => '',
      }
    }

    if (fieldName === 'createdAt') {
      return {
        displayText: 'Date uploaded',
        possibleUnits: ['day(s)', 'week(s)', 'month(s)', 'year(s)', 'hour(s)'],
        possibleOperators: [
          'LAST_30_DAYS',
          'LAST_7_DAYS',
          'EXACTLY',
          'EARLIER_THAN',
          'LATER_THAN',
          'CUSTOM_RANGE',
        ],
        getInitialValue: (operator?: string): DateFilterValue => {
          if (!operator || ['LAST_30_DAYS', 'LAST_7_DAYS'].includes(operator)) {
            return {
              exact: '',
            }
          }
          if (['EXACTLY', 'EARLIER_THAN', 'LATER_THAN'].includes(operator)) {
            return {
              exact: new Date().toISOString(),
            }
          }
          if (['CUSTOM_RANGE'].includes(operator)) {
            return {
              from: new Date().toISOString(),
              to: new Date().toISOString(),
            }
          }
          return {
            exact: new Date().toISOString(),
          }
        },
      }
    }

    if (fieldName === 'updatedAt') {
      return {
        displayText: 'Date last modified',
        possibleUnits: ['day(s)', 'week(s)', 'month(s)', 'year(s)', 'hour(s)'],
        possibleOperators: [
          'LAST_30_DAYS',
          'LAST_7_DAYS',
          'EXACTLY',
          'EARLIER_THAN',
          'LATER_THAN',
          'CUSTOM_RANGE',
        ],
        getInitialValue: (operator?: string): DateFilterValue => {
          if (!operator || ['LAST_30_DAYS', 'LAST_7_DAYS'].includes(operator)) {
            return {
              exact: '',
            }
          }
          if (['EXACTLY', 'EARLIER_THAN', 'LATER_THAN'].includes(operator)) {
            return {
              exact: new Date().toISOString(),
            }
          }
          if (['CUSTOM_RANGE'].includes(operator)) {
            return {
              from: new Date().toISOString(),
              to: new Date().toISOString(),
            }
          }
          return {
            exact: new Date().toISOString(),
          }
        },
      }
    }

    if (fieldName === 'description') {
      return {
        displayText: 'Description',
        possibleOperators: ['HAS'],
        getInitialValue: (operator?: string): DescriptionFilterValue => {
          if (!operator) {
            return ''
          }
          if (['HAS'].includes(operator)) {
            return ''
          }
          return ''
        },
      }
    }

    if (fieldName === 'createdBy') {
      return {
        displayText: 'Created by',
        possibleOperators: ['IS', 'IS_NOT', 'IS_ONE_OF', 'IS_NONE_OF'],
        getInitialValue: (operator?: string): any => {
          if (!operator) {
            return { value: '', label: '' }
          }
          if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
            return []
          } else if (['IS', 'IS_NOT'].includes(operator)) {
            return { value: '', label: '' }
          }
          return { value: '', label: '' }
        },
      }
    }

    if (fieldName === 'path') {
      return {
        displayText: 'Folder',
        possibleOperators: ['IS', 'IS_NOT', 'IS_ONE_OF', 'IS_NONE_OF', 'STARTS_WITH'],
        getInitialValue: (operator?: string): PathFilterValue => {
          if (!operator) {
            return ''
          }
          if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
            return []
          } else if (['IS', 'IS_NOT', 'STARTS_WITH'].includes(operator)) {
            return ''
          }
          return ''
        },
      }
    }
   
    if (fieldName === '"embeddedMetadata.DateTimeOriginal"') {
      return {
        displayText: 'Original Date & Time',
        possibleUnits: ['day(s)', 'week(s)', 'month(s)', 'year(s)', 'hour(s)'],
        possibleOperators: [
          'LAST_30_DAYS',
          'LAST_7_DAYS',
          'EXACTLY',
          'EARLIER_THAN',
          'LATER_THAN',
          'CUSTOM_RANGE',
          'IS_EMPTY',
          'IS_NOT_EMPTY',
        ],
        getInitialValue: (operator?: string): DateFilterValue => {
          if (!operator || ['LAST_30_DAYS', 'LAST_7_DAYS'].includes(operator)) {
            return {
              exact: '',
            }
          }
          if (['EXACTLY', 'EARLIER_THAN', 'LATER_THAN'].includes(operator)) {
            return {
              exact: new Date().toISOString(),
            }
          }
          if (['CUSTOM_RANGE'].includes(operator)) {
            return {
              from: new Date().toISOString(),
              to: new Date().toISOString(),
            }
          }
          if (['IS_EMPTY', 'IS_NOT_EMPTY'].includes(operator)) {
            return null as any
          }
          return {
            exact: new Date().toISOString(),
          }
        },
      }
    }

    if (fieldName === '"embeddedMetadata.Keywords"') {
      return {
        displayText: 'Keywords',
        possibleOperators: ['IS_ONE_OF', 'IS_NONE_OF', 'IS_EMPTY', 'IS_NOT_EMPTY'],
        getInitialValue: (operator?: string): KeywordsFilterValue => {
          if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
            return []
          } else if (['IS_EMPTY', 'IS_NOT_EMPTY'].includes(operator)) {
            return null as any
          }
          return []
        },
      }
    }

    if (fieldName === '"embeddedMetadata.LocationTaken"') {
      return {
        displayText: 'Location',
        possibleOperators: ['GEO_POINT', 'GEO_BOX', 'GEO_POLYGON', 'IS_EMPTY', 'IS_NOT_EMPTY'],
        possibleUnits: ['m', 'KM'],
        getInitialValue: (operator?: string): LocationFilterValue => {
          if (!operator || ['GEO_POINT', 'GEO_BOX', 'GEO_POLYGON'].includes(operator)) {
            return ''
          }
          if (['IS_EMPTY', 'IS_NOT_EMPTY'].includes(operator)) {
            return null as any
          }
          return ''
        },
      }
    }
  }

  if (customMetadataField?.schema) {
    if (['Text', 'Textarea'].includes(customMetadataField.schema.type)) {
      return {
        displayText: customMetadataField.label,
        possibleOperators: [
          'IS',
          'IS_NOT',
          'IS_ONE_OF',
          'IS_NONE_OF',
          'STARTS_WITH',
          'HAS',
          'IS_EMPTY',
          'IS_NOT_EMPTY',
        ],
        getInitialValue: (operator?: string): TextFilterValue => {
          if (!operator) {
            return ''
          }
          if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
            return []
          } else if (['IS', 'IS_NOT', 'STARTS_WITH', 'HAS'].includes(operator)) {
            return ''
          } else if (['IS_EMPTY', 'IS_NOT_EMPTY'].includes(operator)) {
            return null as any
          }
          return ''
        },
      }
    }

    if (customMetadataField.schema.type === 'Date') {
      return {
        displayText: customMetadataField.label,
        possibleUnits: ['day(s)', 'week(s)', 'month(s)', 'year(s)', 'hour(s)'],
        possibleOperators: [
          'LAST_30_DAYS',
          'LAST_7_DAYS',
          'EXACTLY',
          'EARLIER_THAN',
          'LATER_THAN',
          'CUSTOM_RANGE',
          'IS_EMPTY',
          'IS_NOT_EMPTY',
        ],
        getInitialValue: (operator?: string): DateFilterValue => {
          if (!operator || ['LAST_30_DAYS', 'LAST_7_DAYS'].includes(operator)) {
            return {
              exact: '',
            }
          }
          if (['EXACTLY', 'EARLIER_THAN', 'LATER_THAN'].includes(operator)) {
            return {
              exact: new Date().toISOString(),
            }
          }
          if (['CUSTOM_RANGE'].includes(operator)) {
            return {
              from: new Date().toISOString(),
              to: new Date().toISOString(),
            }
          }
          if (['IS_EMPTY', 'IS_NOT_EMPTY'].includes(operator)) {
            return null as any
          }
          return {
            exact: new Date().toISOString(),
          }
        },
      }
    }

    if (['SingleSelect'].includes(customMetadataField.schema.type)) {
      return {
        displayText: customMetadataField.label,
        possibleValues: customMetadataField.schema.selectOptions,
        possibleOperators: ['IS', 'IS_NOT', 'IS_ONE_OF', 'IS_NONE_OF', 'IS_EMPTY', 'IS_NOT_EMPTY'],
        getInitialValue: (operator?: string): SelectFilterValue => {
          if (!operator || ['IS', 'IS_NOT'].includes(operator)) {
            return ''
          } else if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
            return []
          } else if (['IS_EMPTY', 'IS_NOT_EMPTY'].includes(operator)) {
            return null as any
          }
          return ''
        },
      }
    }

    if (['MultiSelect'].includes(customMetadataField.schema.type)) {
      return {
        displayText: customMetadataField.label,
        possibleValues: customMetadataField.schema.selectOptions,
        possibleOperators: ['IS_ONE_OF', 'IS_NONE_OF', 'IS_EMPTY', 'IS_NOT_EMPTY'],
        getInitialValue: (operator?: string): SelectFilterValue => {
          if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
            return []
          } else if (['IS_EMPTY', 'IS_NOT_EMPTY'].includes(operator)) {
            return null as any
          }
          return []
        },
      }
    }

    if (customMetadataField.schema.type === 'Boolean') {
      return {
        displayText: customMetadataField.label,
        possibleOperators: ['IS', 'IS_EMPTY', 'IS_NOT_EMPTY'],
        getInitialValue: (operator?: string): BooleanFilterValue => {
          if (['IS'].includes(operator)) {
            return 'true'
          } else if (['IS_EMPTY', 'IS_NOT_EMPTY'].includes(operator)) {
            return null as any
          }
          return 'true'
        },
      }
    }

    if (customMetadataField.schema.type === 'Number') {
      return {
        displayText: customMetadataField.label,
        possibleOperators: [
          'IS',
          'IS_NOT',
          'LESS_THAN',
          'GREATER_THAN',
          'LESS_THAN_EQUAL',
          'GREATER_THAN_EQUAL',
          'BETWEEN',
          'IS_EMPTY',
          'IS_NOT_EMPTY',
        ],
        getInitialValue: (operator?: string): NumberFilterValue => {
          if (
            !operator ||
            [
              'IS',
              'IS_NOT',
              'GREATER_THAN',
              'LESS_THAN',
              'GREATER_THAN_EQUAL',
              'LESS_THAN_EQUAL',
            ].includes(operator)
          ) {
            return ''
          }

          if (operator === 'BETWEEN') {
            return {
              min: '',
              max: '',
            }
          }

          if (['IS_EMPTY', 'IS_NOT_EMPTY'].includes(operator)) {
            return null as any
          }

          return ''
        },
      }
    }
  }

  return {
    displayText: 'Name',
    possibleOperators: [
      'IS',
      'IS_NOT',
      'IS_ONE_OF',
      'IS_NONE_OF',
      'STARTS_WITH',
    ],
    getInitialValue: (operator?: string): NameFilterValue => {
      if (!operator || ['IS', 'IS_NOT', 'STARTS_WITH'].includes(operator)) {
        return ''
      }
      if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
        return []
      }
      return ''
    },
  }
}

export const getDefaultExpression = ({
  fieldName,
  customMetadataField,
}: FieldIdentifier): SearchFilterJson => {
  if (fieldName) {
    
    if (fieldName === 'name') {
      return {
        fieldName: 'name',
        operator: 'IS',
        value: getFieldsMapping({ fieldName }).getInitialValue('IS'),
      }
    }
    if (fieldName === 'id') {
      return {
        fieldName,
        operator: 'IS',
        value: getFieldsMapping({ fieldName }).getInitialValue(),
      }
    }
    if (fieldName === 'tags') {
      return {
        fieldName: 'tags',
        operator: 'IS_ONE_OF',
        value: getFieldsMapping({ fieldName }).getInitialValue(),
      }
    }
    if (fieldName === 'format') {
      return {
        fieldName: 'format',
        operator: 'IS',
        value: getFieldsMapping({ fieldName }).getInitialValue('IS'),
      }
    }
    if (fieldName === 'private') {
      return {
        fieldName: 'private',
        operator: 'IS',
        value: getFieldsMapping({ fieldName }).getInitialValue(),
      }
    }
    if (fieldName === 'published') {
      return {
        fieldName: 'published',
        operator: 'IS',
        value: getFieldsMapping({ fieldName }).getInitialValue(),
      }
    }
    if (fieldName === 'transparency') {
      return {
        fieldName: 'transparency',
        operator: 'IS',
        value: getFieldsMapping({ fieldName }).getInitialValue(),
      }
    }
    if (fieldName === 'size') {
      return {
        fieldName: 'size',
        operator: 'SLIDER',
        value: getFieldsMapping({ fieldName }).getInitialValue(),
      }
    }
    if (fieldName === 'dimensions') {
      return {
        fieldName: 'dimensions',
        operator: 'IS',
        value: getFieldsMapping({ fieldName }).getInitialValue('IS'),
      }
    }
    if (['createdAt', 'updatedAt'].includes(fieldName)) {
      return {
        fieldName,
        operator: 'LAST_7_DAYS',
        value: getFieldsMapping({ fieldName }).getInitialValue('LAST_7_DAYS'),
      }
    }
    if (fieldName === 'description') {
      return {
        fieldName: 'description',
        operator: 'HAS',
        value: getFieldsMapping({ fieldName }).getInitialValue('HAS'),
      }
    }
    if (fieldName === 'createdBy') {
      return {
        fieldName: 'createdBy',
        operator: 'IS',
        value: getFieldsMapping({ fieldName }).getInitialValue('IS'),
      }
    }
    if (fieldName === 'path') {
      return {
        fieldName: 'path',
        operator: 'IS',
        value: getFieldsMapping({ fieldName }).getInitialValue('IS') as PathFilterValue,
      }
    }
    if (fieldName === '"embeddedMetadata.DateTimeOriginal"') {
      return {
        fieldName,
        operator: 'LAST_7_DAYS',
        value: getFieldsMapping({ fieldName }).getInitialValue('LAST_7_DAYS'),
      }
    }
    if (fieldName === '"embeddedMetadata.Keywords"') {
      return {
        fieldName,
        operator: 'IS_ONE_OF',
        value: getFieldsMapping({ fieldName }).getInitialValue(),
      }
    }
    if (fieldName === '"embeddedMetadata.LocationTaken"') {
      return {
        fieldName,
        operator: 'GEO_POINT',
        value: getFieldsMapping({ fieldName }).getInitialValue(),
      }
    }
  }

  if (customMetadataField?.schema) {
    if (['Text', 'Textarea'].includes(customMetadataField.schema.type)) {
      return {
        fieldName: customMetadataField.name,
        operator: 'IS',
        value: getFieldsMapping({ customMetadataField }).getInitialValue('IS'),
      }
    }
    if (customMetadataField.schema.type === 'Date') {
      return {
        fieldName: customMetadataField.name,
        operator: 'LAST_7_DAYS',
        value: getFieldsMapping({ customMetadataField }).getInitialValue(
          'LAST_7_DAYS',
        ),
      }
    }
    if (['SingleSelect'].includes(customMetadataField.schema.type)) {
      return {
        fieldName: customMetadataField.name,
        operator: 'IS',
        value: getFieldsMapping({ customMetadataField }).getInitialValue('IS'),
      }
    }
    if (['MultiSelect'].includes(customMetadataField.schema.type)) {
      return {
        fieldName: customMetadataField.name,
        operator: 'IS_ONE_OF',
        value: getFieldsMapping({ customMetadataField }).getInitialValue(
          'IS_ONE_OF',
        ),
      }
    }
    if (customMetadataField.schema.type === 'Boolean') {
      return {
        fieldName: customMetadataField.name,
        operator: 'IS',
        value: getFieldsMapping({ customMetadataField }).getInitialValue(),
      }
    }
    if (customMetadataField.schema.type === 'Number') {
      return {
        fieldName: customMetadataField.name,
        operator: 'IS',
        value: getFieldsMapping({ customMetadataField }).getInitialValue('IS'),
      }
    }
  }

  return {
    fieldName: 'name',
    operator: 'IS',
    value: getFieldsMapping({ fieldName: 'name' }).getInitialValue('IS'),
  }
}

export const validateNameFilter = ({
  value,
  operator,
}: {
  value: NameFilterValue
  operator: string
}): FilterValidationObject => {
  if (
    ['IS', 'IS_NOT', 'STARTS_WITH', 'HAS'].includes(operator) &&
    (value as string).trim() === ''
  ) {
    return {
      isValid: false,
      error: 'Enter a non-empty value',
    }
  } else if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
    if (value.length === 0) {
      return {
        isValid: false,
        error: 'Select at least one value',
      }
    }
  }
  return {
    isValid: true,
    error: null,
  }
}

export const validateIdFilter = ({
  value,
  operator,
}: {
  value: IdFilterValue
  operator: string
}): FilterValidationObject => {
  if (['IS', 'IS_NOT'].includes(operator)) {
    const idValue = value as string
    if (idValue.trim() === '') {
      return {
        isValid: false,
        error: 'Enter a non-empty value',
      }
    }
    // Validate single ObjectId value
    if (typeof idValue !== 'string' || !CheckValidObjectId(idValue)) {
      return {
        isValid: false,
        error: 'Invalid Id value',
      }
    }
  } else if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
    if (value.length === 0) {
      return {
        isValid: false,
        error: 'Select at least one value',
      }
    }
    // Validate each ObjectId in the array
    const invalidIds = (value as string[]).filter(
      id => typeof id !== 'string' || !CheckValidObjectId(id),
    )
    if (invalidIds.length > 0) {
      return {
        isValid: false,
        error: 'Invalid Id value(s)',
      }
    }
  }
  return {
    isValid: true,
    error: null,
  }
}



const validateTagsFilter = ({
  value,
  operator,
}: {
  value: TagsFilterValue
  operator: string
}): FilterValidationObject => {
  if (
    ['HAS'].includes(operator) &&
    (value as string).trim() === ''
  ) {
    return {
      isValid: false,
      error: 'Enter a non-empty value',
    }
  } else if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
    if (value.length === 0) {
      return {
        isValid: false,
        error: 'Select at least one value',
      }
    }
  }
  return {
    isValid: true,
    error: null,
  }
}

const validateKeywordsFilter = ({
  value,
  operator,
}: {
  value: KeywordsFilterValue
  operator: string
}): FilterValidationObject => {
  if (operator === "IS_EMPTY" || operator === "IS_NOT_EMPTY") {
    return {
      isValid: true,
      error: null,
    }
  }
  if (!value || value.length === 0) {
    return {
      isValid: false,
      error: 'Select at least one value',
    }
  }
  return {
    isValid: true,
    error: null,
  }
}

const validateTransparencyFilter = ({
  value,
}: {
  value: TransparencyFilterValue
}): FilterValidationObject => {
  if (value.trim() === '') {
    return {
      isValid: false,
      error: 'Select a valid value',
    }
  }
  return {
    isValid: true,
    error: null,
  }
}

const validatePrivacyFilter = ({
  value,
}: {
  value: PrivacyFilterValue
}): FilterValidationObject => {
  if (typeof value === "string" && value.trim() === '') {
    return {
      isValid: false,
      error: 'Select a valid value',
    }
  }
  return {
    isValid: true,
    error: null,
  }
}

const validatePublishedFilter = ({
  value,
}: {
  value: PublishedFilterValue
}): FilterValidationObject => {
  if (value.trim() === '') {
    return {
      isValid: false,
      error: 'Select a valid value',
    }
  }
  return {
    isValid: true,
    error: null,
  }
}

export const validateFormatFilter = ({
  value,
  operator,
}: {
  value: FormatFilterValue
  operator: string
}): FilterValidationObject => {
  if (['IS', 'IS_NOT'].includes(operator) && (value as string).trim() === '') {
    return {
      isValid: false,
      error: 'Enter a non-empty value',
    }
  } else if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
    if (value.length === 0) {
      return {
        isValid: false,
        error: 'Select at least one value',
      }
    }
  }
  return {
    isValid: true,
    error: null,
  }
}

export const validateSelectFilter = ({
  value,
  operator,
}: {
  value: SelectFilterValue
  operator: string
}): FilterValidationObject => {
  if (['IS', 'IS_NOT'].includes(operator)) {
    if (value === '' || typeof value === 'undefined') {
      return {
        isValid: false,
        error: 'Select at least one value',
      }
    }
  }

  if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator) && Array.isArray(value)) {
    if (value.length === 0) {
      return {
        isValid: false,
        error: 'Select at least one value',
      }
    }
  }
  return {
    isValid: true,
    error: null,
  }
}

export const validateDimensionsFilter = ({
  value,
  operator,
}: {
  value: DimensionsFilterValue
  operator: string
}): FilterValidationObject => {
  if (['IS'].includes(operator)) {
    if (typeof value === 'string' && value.trim() === '') {
      return {
        isValid: false,
        error: 'Enter a non-empty value',
      }
    }
  } else if (['CUSTOM_DIMENSIONS'].includes(operator)) {
    if (typeof value !== 'string' && !value.width.min && !value.height.min) {
      return {
        isValid: false,
        error: 'Enter at least one value',
      }
    }
  }
  return {
    isValid: true,
    error: null,
  }
}

export const validateDateFilter = ({
  value,
  operator,
}: {
  value: DateFilterValue
  operator: string
}): FilterValidationObject => {
  if (operator === 'CUSTOM_RANGE') {
    if ('to' in value && 'from' in value && (!value.to || !value.from)) {
      return {
        isValid: false,
        error: 'Pick a complete date range',
      }
    }
  }
  return {
    isValid: true,
    error: null,
  }
}

const validateSizeFilter = (): FilterValidationObject => ({
  isValid: true,
  error: null,
})

const validateBooleanFilter = ({
  value,
}: {
  value: BooleanFilterValue
}): FilterValidationObject => {
  if (!['true', 'false'].includes(value)) {
    return {
      isValid: false,
      error: 'Enter a valid value',
    }
  }
  return {
    isValid: true,
    error: null,
  }
}

const validateCoordinatesString = (coordinatesString: string): boolean => {
  const coordinatesStringArray = coordinatesString.split('|')
  if (coordinatesStringArray.length === 0) {
    return false
  }
  for (const coordinatesPairString of coordinatesStringArray) {
    if (!coordinateRegex.test(coordinatesPairString)) {
      return false
    }
  }
  return true
}

const extractCoordinates = (coordinatesString: string): number[][] => {
  const coordinatesStringArray = coordinatesString.split('|')
  const coordinatesArray = coordinatesStringArray.map(coordinateString => {
    const [latitudeString, longitudeString] = coordinateString.split(',')
    const latitude = Number.parseFloat(latitudeString)
    const longitude = Number.parseFloat(longitudeString)
    return [longitude, latitude]
  })
  return coordinatesArray
}

const validateLocationFilter = ({
  value,
  operator,
}: {
  value: LocationFilterValue
  operator: string
}): FilterValidationObject => {
  if (operator === "IS_EMPTY" || operator === "IS_NOT_EMPTY") {
    return {
      isValid: true,
      error: null,
    }
  }

  if (typeof value !== 'string') {
    return {
      isValid: false,
      error: 'Enter a non-empty value',
    }
  }

  if (operator === 'GEO_POINT') {
    // values like "50,100 5km"
    const [coordinatesString, maxDistanceString, ...invalid] = value.split(' ')
    if (
      invalid.length ||
      !distanceRegex.test(maxDistanceString) ||
      !validateCoordinatesString(coordinatesString) ||
      extractCoordinates(coordinatesString).length !== 1
    ) {
      return {
        isValid: false,
        error: 'Enter a valid value',
      }
    }
  } else if (
    operator === 'GEO_BOX' &&
    (!validateCoordinatesString(value) ||
      extractCoordinates(value).length !== 2)
  ) {
    return {
      isValid: false,
      error: 'Enter a valid value',
    }
  } else if (
    operator === 'GEO_POLYGON' &&
    (!validateCoordinatesString(value) ||
      extractCoordinates(value).length !== 4)
  ) {
    return {
      isValid: false,
      error: 'Enter a valid value',
    }
  }

  return {
    isValid: true,
    error: null,
  }
}

const validateNumberFilter = ({
  value,
  operator,
}: {
  value: NumberFilterValue
  operator: string
}): FilterValidationObject => {
  if (
    [
      'IS',
      'IS_NOT',
      'LESS_THAN',
      'GREATER_THAN',
      'LESS_THAN_EQUAL',
      'GREATER_THAN_EQUAL',
    ].includes(operator) &&
    typeof value === 'string'
  ) {
    if (!numericRegex.test(value)) {
      return {
        isValid: false,
        error: 'Enter a numerical value',
      }
    }
  }

  if (['BETWEEN'].includes(operator) && typeof value !== 'string') {
    if (!numericRegex.test(value.min) || !numericRegex.test(value.max)) {
      return {
        isValid: false,
        error: 'Enter numerical values',
      }
    }
  }

  return {
    isValid: true,
    error: null,
  }
}

export const validateDescriptionFilter = ({
  value,
}: {
  value: DescriptionFilterValue
}): FilterValidationObject => {
  if (typeof value !== 'string' || value.trim() === ''){
    return {
      isValid: false,
      error: 'Enter a valid non-empty value',
    }
  }
  return {
    isValid: true,
    error: null,
  }
}

export const validatePathFilter = ({
  value,
  operator,
}: {
  value: PathFilterValue
  operator: string
}): FilterValidationObject => {
  if (
    ['IS', 'IS_NOT', 'STARTS_WITH'].includes(operator) &&
    (value as string).trim() === ''
  ) {
    return {
      isValid: false,
      error: 'Select at least one folder',
    }
  } else if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
    if (value.length === 0) {
      return {
        isValid: false,
        error: 'Select at least one folder',
      }
    }
  }
  return {
    isValid: true,
    error: null,
  }
}

export const validateFilter = ({
  expression,
  customMetadataField,
}: {
  expression: SearchFilterJson
  customMetadataField?: CustomMetadataResponse
}): {
  isValid: boolean
  error: string | null
} => {

  if (expression.operator === "IS_EMPTY" || expression.operator === "IS_NOT_EMPTY") {
    return {
      isValid: true,
      error: null,
    }
  }

  if (expression.fieldName === 'id') {
    return validateIdFilter({
      value: expression.value as IdFilterValue,
      operator: expression.operator,
    })
  }

  if (expression.fieldName === 'name') {
    return validateNameFilter({
      value: expression.value as NameFilterValue,
      operator: expression.operator,
    })
  }

  if (expression.fieldName === 'tags') {
    return validateTagsFilter({
      value: expression.value as TagsFilterValue,
      operator: expression.operator,
    })
  }

  if (expression.fieldName === 'transparency') {
    return validateTransparencyFilter({
      value: expression.value as TransparencyFilterValue,
    })
  }

  if (expression.fieldName === 'private') {
    return validatePrivacyFilter({
      value: expression.value as PrivacyFilterValue,
    })
  }

  if (expression.fieldName === 'published') {
    return validatePublishedFilter({
      value: expression.value as PublishedFilterValue,
    })
  }

  if (expression.fieldName === 'format') {
    return validateFormatFilter({
      value: expression.value as FormatFilterValue,
      operator: expression.operator,
    })
  }

  if (expression.fieldName === 'dimensions') {
    return validateDimensionsFilter({
      value: expression.value as DimensionsFilterValue,
      operator: expression.operator,
    })
  }

  if (expression.fieldName === 'size') {
    return validateSizeFilter()
  }

  if (
    ['createdAt', 'updatedAt', '"embeddedMetadata.DateTimeOriginal"'].includes(
      expression.fieldName,
    )
  ) {
    return validateDateFilter({
      value: expression.value as DateFilterValue,
      operator: expression.operator,
    })
  }

  if (expression.fieldName === 'description'){
    return validateDescriptionFilter({
      value: expression.value as DescriptionFilterValue,
    })
  }

  if (expression.fieldName === 'path'){
    return validatePathFilter({
      value: expression.value as PathFilterValue,
      operator: expression.operator,
    })
  }

  if (expression.fieldName === 'createdBy') {
    const { operator, value } = expression
    if (['IS', 'IS_NOT'].includes(operator)) {
      const strVal = typeof value === 'string' ? value : (value as { value: string; label: string })?.value
      if (!strVal || strVal.trim() === '') {
        return { isValid: false, error: 'Value is required' }
      }
      return { isValid: true, error: null }
    }
    if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
      if (!Array.isArray(value) || value.length === 0) {
        return { isValid: false, error: 'At least one value is required' }
      }
      return { isValid: true, error: null }
    }
    return { isValid: true, error: null }
  }

  if (expression.fieldName === '"embeddedMetadata.Keywords"') {
    return validateKeywordsFilter({
      value: expression.value as KeywordsFilterValue,
      operator: expression.operator,
    })
  }

  if (expression.fieldName === '"embeddedMetadata.LocationTaken"') {
    return validateLocationFilter({
      value: expression.value as LocationFilterValue,
      operator: expression.operator,
    })
  }

  if (customMetadataField?.schema) {
    if (customMetadataField.schema.type === 'Date') {
      return validateDateFilter({
        value: expression.value as DateFilterValue,
        operator: expression.operator,
      })
    }

    if (['Text', 'Textarea'].includes(customMetadataField.schema.type)) {
      return validateNameFilter({
        value: expression.value as TextFilterValue,
        operator: expression.operator,
      })
    }

    if (
      ['SingleSelect', 'MultiSelect'].includes(customMetadataField.schema.type)
    ) {
      return validateSelectFilter({
        value: expression.value as SelectFilterValue,
        operator: expression.operator,
      })
    }

    if (customMetadataField.schema.type === 'Boolean') {
      return validateBooleanFilter({
        value: expression.value as BooleanFilterValue,
      })
    }

    if (customMetadataField.schema.type === 'Number') {
      return validateNumberFilter({
        value: expression.value as NumberFilterValue,
        operator: expression.operator,
      })
    }
  }

  return {
    isValid: true,
    error: null,
  }
}

const mimeTypeMap = {
  'image/webp': 'webp',
  'image/png': 'png',
  'image/jpg': 'jpg',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/svg+xml': 'svg',
  'image/avif': 'avif',
  'image/heif': 'heif',
  'image/heic': 'heic',
}

const staticMimeTypeMap = {
  'application/pdf': 'pdf',
  'text/javascript': 'js',
  'application/javascript': 'js',
  'application/x-javascript': 'js',
  'font/woff2': 'woff2', // font files will have application/octet-stream though
  'font/woff': 'woff',
  'font/ttf': 'ttf',
  'font/otf': 'otf',
  'application/vnd.ms-fontobject': 'eot',
  'text/js': 'js',
  'text/css': 'css',
  'text/plain': 'txt',
  'video/mp4': 'mp4',
  'video/x-m4v': 'm4v',
  'video/webm': 'webm',
  'video/quicktime': 'mov',
  'application/x-shockwave-flash': 'swf',
  'video/MP2T': 'ts',
  'application/x-mpegURL': 'm3u8',
  'application/x-mpegurl': 'm3u8',
  'image/x-icon': 'ico',
  'image/vnd.microsoft.icon': 'ico',
  'application/json': 'json',
  'application/xml': 'xml',
  'text/xml': 'xml',
  'audio/mpeg': 'mp3',
  'audio/x-m4a': 'm4a',
  'audio/wav': 'wav',
  'application/x-httpd-php': 'php',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'pptx',
  'image/vnd.adobe.photoshop': 'psd',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/rtf': 'rtf',
  'application/rss+xml': 'rss',
  'text/html': 'html',
  'image/bmp': 'bmp',
  'model/gltf-binary': 'glb',
  'image/vnd.dxf': 'dxf',
  'text/x-python': 'py',
  'model/obj': 'obj',
  'image/x-3ds': '3ds',
  'model/stl': 'stl',
  'image/vnd.djvu': 'djvu',
  'image/x-adobe-dng': 'dng',
  'model/gltf+json': 'gltf',
  'application/x-indesign': 'indd',
  'image/x-sony-arw': 'arw',
  'text/csv': 'csv',
  'text/yaml': 'yaml',
  'application/illustrator': 'ai',
  'application/vnd.adobe.illustrator': 'ai',
  'application/postscript': 'ai',
  'application/eps': 'eps',
}

export const supportedMultiPageFileFormats = ['pdf', 'psd', 'eps', 'ai', 'ept']

export const extensionToIconMap: {
  [key: string]: ComponentWithAs<'svg', IconProps>
} = {
  css: CSSIcon,
  doc: DOCIcon,
  eot: EOTIcon,
  ico: ICOIcon,
  js: JSIcon,
  m3u8: M3U8Icon,
  mov: VideoIcon,
  mp4: VideoIcon,
  m4v: VideoIcon,
  otf: OTFIcon,
  pdf: PDFIcon,
  swf: SWFIcon,
  ttf: TTFIcon,
  ts: TSIcon,
  txt: TXTIcon,
  webm: VideoIcon,
  woff2: WOFF2Icon,
  woff: WOFFIcon,
  xls: XLSIcon,
  json: JSONIcon,
  xml: XMLIcon,
  mp3: AudioIcon,
  m4a: AudioIcon,
  wav: AudioIcon,
}

export const getExtensionFromMimeType = (mimeType?: string): string | null => {
  if (!mimeType) {
    return null
  }

  if (Object.keys(mimeTypeMap).includes(mimeType)) {
    return mimeTypeMap[mimeType as keyof typeof mimeTypeMap]
  }

  if (Object.keys(staticMimeTypeMap).includes(mimeType)) {
    return staticMimeTypeMap[mimeType as keyof typeof staticMimeTypeMap]
  }

  return null
}

export const getExtensionFromURL = (url: string): string | null => {
  return url.split(/[#?]/u)[0].split('.').pop()?.trim() ?? null
}

export const getExtensionFromFileTypeAndMimeType = ({
  mime,
  url,
  fileType,
}: Pick<
  MediaLibrarySearchFileResponse | FileDetailsResponse,
  'url' | 'mime' | 'fileType'
>): string | null => {
  if (mime === 'application/xml') {
    if (fileType === 'image') {
      return 'svg'
    }
    return 'xml'
  }
  const extension = getExtensionFromMimeType(mime)
  if (extension) {
    if (extension === 'js') {
      const extensionFromName = url.split(/[#?]/u)[0].split('.').pop()?.trim()

      if (extensionFromName && ['js', 'mjs'].includes(extensionFromName)) {
        return extensionFromName
      }
      return extension
    }
    return extension
  }

  const urlComponents = url.split(/[#?]/u)[0].split('.')

  if (urlComponents.length > 1) {
    const possibleExtension = urlComponents.pop()?.trim() ?? null
    if (possibleExtension && !possibleExtension.includes('/')) {
      return possibleExtension
    }
  }

  return null
}

export const canAddTags = (
  fileDetail: MediaLibrarySearchFileResponse | FileDetailsResponse,
): boolean => fileDetail.type === 'file'

// Whitelisting to allow custom focus area and image editor operations
export const isEditableImage = (
  fileDetail: MediaLibrarySearchFileResponse | FileDetailsResponse,
): boolean => {
  const extensionFound = getExtensionFromFileTypeAndMimeType(fileDetail)
  const extensionsWhitelist = ['jpg', 'webp', 'png', 'avif', 'ico', 'svg']
  return (
    fileDetail.type === 'file' &&
    extensionsWhitelist.includes(extensionFound ?? '')
  )
}


// Whitelisting to allow extensions (bg-removal and auto-tag)
export const allowExtensionOperations = (
  fileDetail: MediaLibrarySearchFileResponse | FileDetailsResponse,
): boolean => {
  const extensionFound = getExtensionFromFileTypeAndMimeType(fileDetail)
  const extensionsWhitelist = ['jpg', 'webp', 'png', 'avif', 'ico']
  return (
    fileDetail.type === 'file' &&
    extensionsWhitelist.includes(extensionFound ?? '')
  )
}

export const SocialUrlTransforms = {
  facebookPost: { width: '1200', height: '630' },
  facebookStory: { width: '1080', height: '1920' },
  instagramSquarePost: {
    width: '1080',
    height: '1080',
  },
  instagramHorizontalPost: {
    width: '1080',
    height: '566',
  },
  instagramVerticalPost: {
    width: '1080',
    height: '1350',
  },
  instagramStory: { width: '1080', height: '1920' },
  twitterPost: { width: '1200', height: '675' },
  twitterLinkPreview: {
    width: '1200',
    height: '675',
  },
  linkedinPost: { width: '1104', height: '736' },
  linkedLinkPreview: {
    width: '1200',
    height: '628',
  },
}

export const pushInBrowserHistory = ({
  routerHistory,
  routerLocation,
  type,
  hash,
  path,
  state,
}: {
  routerHistory: History
  routerLocation: Location
  type?: string
  hash?: string
  path?: string
  state?: object
}): void => {
  if (hash && type) {
    try {
      routerHistory.push(`?${type}=${hash}`, { [type]: hash })
    } catch (e) {
      console.log(e)
    }
  } else if (path && state) {
    routerHistory.push(path, state)
  } else {
    routerHistory.push(routerLocation.pathname, state)
  }
}

export const encodeStringForUrl = (str: string): string => btoa(encodeURI(str))

export const decodeStringForUrl = (hash: string): string =>
  decodeURI(atob(hash))

export const foldersPerPage = 1000

export const secondaryFoldersPerPage = 100

export const filesPerPage = 1000

export const mediaCollectionsPerPage = 1000

export const isSignedURLRequired = ({
  isPrivateFile,
  isPublished,
  onlyNamedGet,
  onlySignedGet,
}: {
  isPrivateFile: boolean
  isPublished: boolean
  onlySignedGet?: UserSessionResponse["onlySignedGet"]
  onlyNamedGet?: UserSessionResponse["onlyNamedGet"]
}): boolean =>
  isPrivateFile ||
  isPublished === false ||
  onlySignedGet === 'true' ||
  onlyNamedGet === 'true'

export const isSignedVideoURLRequired = ({
  isPrivateFile,
  isPublished,
  onlySignedVideoGet,
  onlyNamedVideoGet,
}: {
  isPrivateFile: boolean
  isPublished: boolean
  onlySignedVideoGet?: UserSessionResponse["onlySignedVideoGet"]
  onlyNamedVideoGet?: UserSessionResponse["onlyNamedVideoGet"]
}): boolean =>
  isPrivateFile ||
  isPublished === false ||
  onlySignedVideoGet === 'true' ||
  onlyNamedVideoGet === 'true' ||
  onlySignedVideoGet === true ||
  onlyNamedVideoGet === true

export const getFileURL = async ({
  file,
  imagekitId,
  firstCustomCname,
  onlySignedGet,
  onlyNamedGet,
  onlySignedVideoGet,
  onlyNamedVideoGet,
  request,
  transformation = [],
  isSignedURLRequiredFn = isSignedURLRequired,
  getCNameVideoThumbnail = false,
  expirationTimestamp,
  urlPrefix,
  urlFor = "",
}: {
  file: MediaLibrarySearchFileResponse | FileDetailsResponse
  imagekitId: string
  firstCustomCname?: UserSessionResponse["firstCustomCname"]
  onlySignedGet?: UserSessionResponse["onlySignedGet"]
  onlyNamedGet?: UserSessionResponse["onlyNamedGet"]
  onlySignedVideoGet?: UserSessionResponse["onlySignedVideoGet"]
  onlyNamedVideoGet?: UserSessionResponse["onlyNamedVideoGet"]
  request: Req<string>
  transformation?: Array<{ [key: string]: string }>
  isSignedURLRequiredFn?: typeof isSignedURLRequired | typeof isSignedVideoURLRequired
  getCNameVideoThumbnail?: boolean
  expirationTimestamp?: number
  urlPrefix: string
  urlFor?: string
}): Promise<string> => {
  const { fileId, isPrivateFile, isPublished, url, updatedAt, thumbnail } = file
  if (isSignedURLRequiredFn({
    isPrivateFile,
    isPublished,
    onlySignedGet,
    onlyNamedGet,
    onlySignedVideoGet,
    onlyNamedVideoGet,
  })) {
    const response = await fetchSignedUrl(
      urlPrefix,
      file?.versionInfo?.id || fileId,
      request,
      transformation,
      {
        updatedAt: new Date(updatedAt).valueOf(),
      },
      expirationTimestamp,
      urlFor
    )
    if ((response as MediaLibraryErrorResponse)?.message) {
      const requiredURL = getCNameVideoThumbnail ? thumbnail : url
      return getCnameUrlIfRequired({
        fileURL: requiredURL,
        imagekitId,
        firstCustomCname,
      })
    }
    return getCnameUrlIfRequired({
      fileURL: response as string,
      imagekitId,
      firstCustomCname,
    })
  }

  const requiredURL = getCNameVideoThumbnail ? thumbnail : url
  return getCnameUrlIfRequired({
    fileURL: requiredURL,
    imagekitId,
    firstCustomCname,
    transformation,
  })
}

export const mediaLibraryThumbnailTransformation = [
  {
    named: 'ik_ml_thumbnail',
  },
]

export const convertDefaultUploadParameters = values => {
  let fileObject: MediaLibraryFormProps = {}

  if (values?.extensions?.length)
    fileObject = { ...fileObject, extensions: values?.extensions }

  // Add saved extensions - keep them separate for UI and also merge into extensions for upload
  if (values?.savedExtensions?.length) {
    // Keep savedExtensions separate for the UI (checkboxes)
    fileObject = { ...fileObject, savedExtensions: values.savedExtensions }
    
    // Also merge into extensions for backward compatibility
    if (fileObject.extensions) {
      fileObject.extensions = [...fileObject.extensions, ...values.savedExtensions]
    } else {
      fileObject.extensions = values.savedExtensions
    }
  }

  if (
    values &&
    (values?.customCoordinates?.['x-coordinate'] ||
      values?.customCoordinates?.['y-coordinate'] ||
      values?.customCoordinates?.height ||
      values?.customCoordinates?.width)
  )
    fileObject = {
      ...fileObject,
      customCoordinates: `${values?.customCoordinates['x-coordinate']},${values?.customCoordinates['y-coordinate']},${values?.customCoordinates.width},${values?.customCoordinates?.height}`,
    }

  if (values?.customMetadata && Array.isArray(values?.customMetadata)) {
    fileObject = {
      ...fileObject,
      customMetadata: values?.customMetadata?.reduce(
        (ob, data) => ({ ...ob, ...data }),
        {},
      ),
    }
  }
  if (values && Object.keys(values)?.includes('uniqueFileNameOnUpload')) {
    fileObject = {
      ...fileObject,
      useUniqueFileName:
        values?.uniqueFileNameOnUpload === 'true' ? true : false,
    }
  }
  if (values && Object.keys(values)?.includes('isPrivateFile')) {
    fileObject = {
      ...fileObject,
      isPrivateFile: values?.isPrivateFile === 'true' ? true : false,
    }
  }
  if (values && Object.keys(values)?.includes('publishFileOnUpload')) {
    fileObject = {
      ...fileObject,
      isPublished: values?.publishFileOnUpload === 'true' ? true : false,
    }
  }
  if (values && Object.keys(values)?.includes('overwriteFile')) {
    fileObject = {
      ...fileObject,
      overwriteFile: values?.overwriteFile === 'true' ? true : false,
    }
  }
  if (values && Object.keys(values)?.includes('overwriteTags')) {
    fileObject = {
      ...fileObject,
      overwriteTags: values?.overwriteTags === 'true' ? true : false,
    }
  }
  if (values && Object.keys(values)?.includes('overwriteDescription')) {
    fileObject = {
      ...fileObject,
      overwriteDescription: values?.overwriteDescription === 'true' ? true : false,
    }
  }
  if (values && Object.keys(values)?.includes('overwriteAITags')) {
    fileObject = {
      ...fileObject,
      overwriteAITags: values?.overwriteAITags === 'true' ? true : false,
    }
  }
  if (values && Object.keys(values)?.includes('overwriteCustomMetadata')) {
    fileObject = {
      ...fileObject,
      overwriteCustomMetadata:
        values?.overwriteCustomMetadata === 'true' ? true : false,
    }
  }
  if (values && Object.keys(values)?.includes('preTransformation')) {
    fileObject = { ...fileObject, preTransformation: values?.preTransformation }
  }
  if (values && Object.keys(values)?.includes('postTransformation')) {
    fileObject = { ...fileObject, postTransformation: values?.postTransformation }
  }
  return fileObject
}

export const checkAllowedPostTransformation = (postTransformation, type) => {
  if (type && mimeTypeMap[type] === 'gif' && postTransformation?.length) {
    return postTransformation.filter(
      data =>
        data.type === PostTransformationTypes.Transformation ||
        data.type === PostTransformationTypes.GifToVideo,
    )
  }
  if (
    type &&
    fileConstraints.allowedFiles.image.includes(type) &&
    postTransformation?.length
  ) {
    return postTransformation.filter(
      data => data.type === PostTransformationTypes.Transformation,
    )
  }
  if (
    type &&
    fileConstraints.allowedFiles.video.includes(type) &&
    postTransformation?.length
  ) {
    return postTransformation.filter(
      data =>
        data.type === PostTransformationTypes.Transformation ||
        data.type === PostTransformationTypes.Thumbnail ||
        data.type === PostTransformationTypes.ABS,
    )
  }
  return null
}

export const formatTags = (
  options: Option[] | OnChangeValue<Option, true>,
): Option[] | OnChangeValue<Option, true> => {
  // to make comma separated input as multiple tags
  let updatedOptions = Array.isArray(options)
    ? options.filter(tagOb => tagOb.__isNew__ !== true)
    : options
  if (Array.isArray(options) && options?.length) {
    const newTag = options.filter(tagOb => tagOb.__isNew__ === true)
    for(let tg of newTag){
      if (tg && tg?.label.includes(',')) {
        const newData = tg.label
          .split(',')
          ?.filter(val => val !== '')
          ?.map(val => ({ label: val, value: val }))
        updatedOptions = [...updatedOptions, ...newData]
      } else if (tg) updatedOptions = [...updatedOptions, tg]
    }
  }
  return updatedOptions
}

export const TypeBadge = ({
  val,
}: {
  val: string | number | boolean
}): JSX.Element => (
  <Badge textTransform="lowercase" fontWeight="medium" textColor="appBlack600">
    {typeof val}
  </Badge>
)

// Checking whether the file will be previewed in ImagePreview component
export const willUseImagePreview = (
  fileDetail: MediaLibrarySearchFileResponse | FileDetailsResponse,
): boolean => {
  const extensionFound = getExtensionFromFileTypeAndMimeType(fileDetail)
  const extensionsWhitelist = [
    'jpg',
    'jpeg',
    'png',
    'webp',
    'gif',
    'svg',
    'heic',
    'heif',
    'avif',
    'ai',
    'eps',
    'psd',
    'ept',
  ]
  return (
    (fileDetail.type === 'file' || fileDetail.type === 'file-version') &&
    extensionsWhitelist.includes(extensionFound ?? '')
  )
}

export const downloadFileFromUrl = (url: string) => {
  // Extract the relative path from the URL
  const parsedIntialUrl = new URL(url)
  const relativePath = parsedIntialUrl.pathname + parsedIntialUrl.search // Combine path and query string

  fetch(relativePath, {
    method: 'GET',
    credentials: 'include', // Include cookies for authentication
  })
    .then(response => response.text())
    .then((text) => {
      const downloadUrl = new URL(text)
      downloadUrl.searchParams.set("ik-attachment", "true")
      const link = document.createElement('a')
      link.href = downloadUrl.href
      link.download = ""
      link.target = "_blank"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    })
    .catch(error => console.error('Download error:', error))
}